import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAppSelector } from '@/redux/Store';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  joinThread: (threadId: string) => void;
  leaveThread: (threadId: string) => void;
  sendMessage: (threadId: string, message: any) => void;
  sendTyping: (threadId: string) => void;
  sendStopTyping: (threadId: string) => void;
  markMessageAsRead: (threadId: string, messageId: string) => void;
  markThreadAsRead: (threadId: string) => void;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  joinThread: () => {},
  leaveThread: () => {},
  sendMessage: () => {},
  sendTyping: () => {},
  sendStopTyping: () => {},
  markMessageAsRead: () => {},
  markThreadAsRead: () => {},
});

export const useSocket = () => useContext(SocketContext);

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const token = useAppSelector((state) => state.auth.accessToken);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    // Only connect if user is authenticated and has a token
    if (!isAuthenticated || !token) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Don't reconnect if already connected
    if (socketRef.current?.connected) {
      return;
    }

    // Remove /api suffix if present since Socket.IO connects to base URL
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';
    const serverUrl = apiUrl.replace(/\/api$/, '');

    // Create socket connection with authentication
    try {
      const newSocket = io(serverUrl, {
        auth: {
          token,
        },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
        forceNew: true,
        timeout: 10000,
      });

    // Connection event handlers
    newSocket.on('connect', () => {
      setIsConnected(true);
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
      setIsConnected(false);
    });

    socketRef.current = newSocket;
    setSocket(newSocket);
  } catch (error) {
    console.error('Error creating socket:', error);
    setIsConnected(false);
  }

    // Cleanup on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [isAuthenticated, token]);

  const joinThread = (threadId: string) => {
    if (socket?.connected) {
      socket.emit('join-thread', threadId);
    }
  };

  const leaveThread = (threadId: string) => {
    if (socket?.connected) {
      socket.emit('leave-thread', threadId);
    }
  };

  const sendMessage = (threadId: string, message: any) => {
    if (socket?.connected) {
      socket.emit('new-message', { threadId, message });
    }
  };

  const sendTyping = (threadId: string) => {
    if (socket?.connected) {
      socket.emit('typing', { threadId, username: user?.username });
    }
  };

  const sendStopTyping = (threadId: string) => {
    if (socket?.connected) {
      socket.emit('stop-typing', { threadId, username: user?.username });
    }
  };

  const markMessageAsRead = (threadId: string, messageId: string) => {
    if (socket?.connected) {
      socket.emit('message-read', { threadId, messageId });
    }
  };

  const markThreadAsRead = (threadId: string) => {
    if (socket?.connected) {
      socket.emit('thread-read', threadId);
    }
  };

  const value: SocketContextType = {
    socket,
    isConnected,
    joinThread,
    leaveThread,
    sendMessage,
    sendTyping,
    sendStopTyping,
    markMessageAsRead,
    markThreadAsRead,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};
